import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = `/aiconversation/<id>/chatMessage`;

interface QueryParams {
	id: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}

export function getConversationChatMessageQueryKey(queryParams: QueryParams): any[] {
	// Extract only the plain data needed from impersonationContext to avoid circular references
	// when the MobX observable is serialized
	const impersonationKey = queryParams.impersonationContext
		? {
				accountId: queryParams.impersonationContext.account?.id,
				userId: queryParams.impersonationContext.user?.id,
			}
		: undefined;
	return [BASE_QUERY_KEY, queryParams.id, impersonationKey];
}

export function useAIConversationChatMessageQuery(queryParams: {
	id: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
	initialData?: Api.AIChatMessage[];
	staleTime?: number;
}) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: getConversationChatMessageQueryKey(queryParams),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.AIChatMessage[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `aiconversation/${queryParams.id}/chatMessage`,
					impersonationContext: queryParams.impersonationContext,
				}),
				'GET'
			),
		enabled: queryParams.enabled,
		initialData: queryParams.initialData,
		staleTime: queryParams.staleTime,
	});
}

export function getLastAIConversationChatMessage(
	conversationId: string,
	impersonationContext?: Api.IImpersonationContext
) {
	const messages = reactQueryClient.getQueryData<Api.AIChatMessage[]>(
		getConversationChatMessageQueryKey({
			id: conversationId,
			impersonationContext,
		})
	);
	return messages?.[messages.length - 1];
}

export function updateMessageInConversationCache(queryKey: any, message: Api.AIChatMessage) {
	const messages = reactQueryClient.getQueryData<Api.AIChatMessage[]>(queryKey);
	if (messages) {
		reactQueryClient.setQueryData(queryKey, (oldMessages: Api.AIChatMessage[]) => {
			return oldMessages.map(oldMessage => {
				if (oldMessage.id === message.id) {
					return message;
				}
				return oldMessage;
			});
		});
	}
}

export function invalidateAIConversationChatMessage(conversationId: string) {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY, conversationId] });
}
